import type { PostProcessorModule } from 'i18next'
import sentry from 'utils/sentry/sentry'

type MissingTranslation = {
  language: string
  namespace: string
  key: string
  fallbackLanguage?: string
}

type MissingKeyReporterOptions = {
  hasLoaded: (language: string, namespace: string) => boolean
  report: (missing: MissingTranslation) => void
  reportFallbackFor: readonly string[]
}

type MissingKeyReporter = {
  handleMissingKey: (languages: readonly (string | undefined)[], namespace: string, key: string) => void
  fallbackReporter: PostProcessorModule
}

type ResolvedLookup = {
  res?: unknown
  usedLng?: string
  usedNS?: string
  usedKey?: string
  exactUsedKey?: string
}

const SENTRY_TAG_VALUE_MAX_LENGTH = 200

function isResolvedLookup(value: unknown): value is ResolvedLookup {
  return typeof value === 'object' && value !== null
}

/**
 * Builds the i18next hooks that report each missing translation once per language, namespace and key:
 * `handleMissingKey` for a key no language has, and `fallbackReporter` for a key that a language in
 * `reportFallbackFor` lacks but the fallback language supplies.
 */
export function createMissingKeyReporter({
  hasLoaded,
  report,
  reportFallbackFor,
}: MissingKeyReporterOptions): MissingKeyReporter {
  const reported = new Set<string>()

  function reportOnce(missing: MissingTranslation): void {
    if (!hasLoaded(missing.language, missing.namespace)) return

    const reportId = JSON.stringify([missing.language, missing.namespace, missing.key])
    if (reported.has(reportId)) return

    reported.add(reportId)
    report(missing)
  }

  return {
    handleMissingKey: (languages, namespace, key) => {
      for (const language of languages) {
        if (language !== undefined) reportOnce({ language, namespace, key })
      }
    },
    fallbackReporter: {
      type: 'postProcessor',
      name: 'reportFallbackTranslations',
      process: (value, _key, options, translator) => {
        const language: string | undefined = options.lng ?? translator.language
        const resolved: unknown = options.i18nResolved
        if (!isResolvedLookup(resolved) || resolved.res === undefined) return value

        const fellBack = resolved.usedLng !== undefined && resolved.usedLng !== language
        if (language && fellBack && reportFallbackFor.includes(language) && resolved.usedNS) {
          reportOnce({
            language,
            namespace: resolved.usedNS,
            key: resolved.exactUsedKey ?? resolved.usedKey ?? value,
            fallbackLanguage: resolved.usedLng,
          })
        }
        return value
      },
    },
  }
}

/**
 * Sends a missing translation to Sentry, grouped into one issue per namespace with the key as a tag.
 */
export function reportMissingTranslationToSentry(translationSource: string): (missing: MissingTranslation) => void {
  return ({ language, namespace, key, fallbackLanguage }) => {
    sentry.captureScopedMessage({
      scope: 'i18n',
      issueType: 'missing_translation_key',
      message: 'Missing translation key',
      level: 'info',
      tags: {
        translation_source: translationSource,
        language,
        fallback_language: fallbackLanguage ?? 'none',
        i18n_namespace: namespace,
        i18n_key: key.slice(0, SENTRY_TAG_VALUE_MAX_LENGTH),
      },
      extra: { key },
      fingerprint: ['i18n', 'missing_translation_key', namespace],
    })
  }
}
